<template>
  <div class="px-2 card-payment-information">
    <v-card outlined :id="id">
      <v-card-title class="payment-card-title">
        {{titleText}}
      </v-card-title>
      <v-alert
        class="alert-notification"
        v-if="alert"
        :color="alertType === 'success' ? 'green' : 'red'"
        :type="alertType"
      >
        {{ alertMsg }}
      </v-alert>
      <!-- Payment Information -->
      <v-card-text class="pb-0 px-10">
        <div class="mx-3">
          <div class="px-10 ml-0">
            <v-text-field
              class="cardholder-name"
              :class="{ 'cardholder-required': !cardHolderName && isNameDirty }"
              hide-details
              v-model="cardHolderName"
              :outlined="outlined"
              label="Cardholder's Name"
              :dense="outlined"
              @input="onInputChange"
              @blur="isNameDirty = true"
              autofocus
            >
            </v-text-field>
          </div>
          <div class="py-2 ml-0 error-msg">
            <div
              v-if="!cardHolderName && isNameDirty"
              id="card-errors"
              role="alert"
            >
              This field is required.
            </div>
          </div>
        </div>

        <div class="mx-2">
          <!-- stripe.js Credit Card Widget (Disabled) due to lack of support on ShadowRoot DOMs -->
          <div class="d-none">
            <label for="card-element"></label>
            <div
              ref="cardElement"
              id="card-element"
              class="stripe-credit-card-element w-100"
              :class="{ 'outlined-field': outlined }"
            >
              <!-- Elements will create input elements here -->
            </div>

            <div class="py-2 error-msg">
              <div id="card-errors" role="alert">
                <li v-if="errorMessage && errorMessage.length">
                  {{ errorMessage }}
                </li>
                <li v-if="isCardEmpty">This field is required.</li>
              </div>
            </div>
          </div>

          <!-- Stripe Credit Card Component Wrapper which supports ShadowRoot DOMs -->
          <stripe-card
            ref="stripeCard"
            class="stripe-card"
            :publishable-key="STRIPE_KEY"
            @token-changed="onTokenChange"
            @loading-changed="onLoadingChange"
            @input="onCardInput"
            :error="error"
            hide-submit
            hide-zip
          >
          </stripe-card>

          <div v-if="dataErrors && dataErrors.length" class="error-msg">
            {{ dataErrors }}
          </div>
        </div>
      </v-card-text>

      <!-- Payment Addition -->
      <v-card-actions class="pt-0 pb-3">
        <div class="payment-card-footer">
          <v-btn
            depressed
            color="primary"
            @click="onClick"
            :disabled="!isDirty"
            :loading="saveLoading"
          >
            {{actionText}}
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
    <notifications
      group="stripeCreditCardInformation"
      position="bottom right"
    />
  </div>
</template>

<script type="text/babel">
import {
  VRow,
  VCol,
  VCard,
  VCardTitle,
  VCardText,
  VCardActions,
  VAlert,
  VTextField,
  VBtn
} from "vuetify/lib";

import _set from "lodash/set";
import config from "@/config";

import { loadStripe } from "@stripe/stripe-js";

import StripeCard from "@morbidick/stripe-elements/stripe-card";

const STRIPE_KEY = config.stripe.key;

export default {
  name: "components-json_page-components-stripe-credit-card",
  watch: {
    alert(new_val) {
      if (new_val) {
        setTimeout(() => (this.alert = false), 3500);
      }
    },
    errors(newVal) {
      this.dataErrors = newVal;
    }
  },
  props: {
    id: undefined,
    value: undefined,
    outlined: {
      type: Boolean,
      default: false
    },
    titleText: {
      type: String,
      default: 'Payment Information'      
    },
    actionText: {
      type: String,
      default: 'Verify'
    },
    errors: {
      default() {
        return "";
      }
    }
  },
  data() {
    return {
      STRIPE_KEY,
      token: undefined,
      stripe: undefined,
      elements: undefined,
      card: undefined,
      errorMessage: "",
      complete: false,
      saveLoading: false,
      cardHolderName: undefined,
      isDirty: true,
      isNameDirty: false,
      isCardEmpty: false,
      cardElement: undefined,
      isStripeMount: false,
      componentTimeout: undefined,
      alert: false,
      alertType: "success",
      alertMsg: "",
      error: undefined,
      dataErrors: ""
    };
  },
  methods: {
    showNotification(title, text, type = "success") {
      this.$notify({
        group: "stripeCreditCardInformation",
        type,
        title,
        text,
        duration: 3000,
        speed: 1000
      });
    },
    onInputChange() {
      this.dataErrors = "";
      this.isDirty = true;
      this.isNameDirty = true;
      if (this.token) {
        this.token = undefined;
        this.$emit("input", this.token);
      }
    },
    async createSource() {
      this.saveLoading = true;
      await this.stripe
        .createSource(this.card, {
          type: "card",
          owner: {
            name: this.cardHolderName
          }
        })
        .then(result => {
          if (result.error) {
            this.errorMessage = result.error.message;
            this.isAddingNew = true;
          } else {
            this.token = result.source;
            this.$emit("input", this.token);
          }
        })
        .catch(error => {
          console.log("Unable to add credit card", error);
        })
        .finally(() => {
          this.isDirty = false;
          this.saveLoading = false;
        });
    },
    async initStripe() {
      if (!this.isStripeMount) {
        this.stripe = await loadStripe(STRIPE_KEY);
        this.elements = this.stripe.elements();

        this.card = this.elements.create("card", {
          style: {
            base: {
              fontSize: "16px",
              fontWeight: 400,
              fontFamily: "sans-serif",
              fontSmoothing: "antialiased",
              ":focus": {
                iconColor: "#0e8dc4"
              },
              "::placeholder": {
                color: "#0e8dc4"
              }
            }
          }
        });

        this.card.on("blur", () => {
          this.isCardEmpty = this.cardElement.classList.contains(
            "StripeElement--empty"
          );
        });

        this.card.on("change", ({ error, complete, empty }) => {
          this.isCardEmpty = empty;
          this.isDirty = true;
          this.complete = complete;
          this.errorMessage = error ? error.message : "";

          if (this.token && !this.complete) {
            this.token = undefined;
            this.$emit("input", this.token);
          }
        });
      }
    },
    loadStripeWidget() {
      this.cardElement = this.$refs.cardElement;
      if (this.cardElement && !this.isStripeMount) {
        this.isStripeMount = true;
        this.card.mount(this.cardElement);
      }
    },
    async setStripeCreditCard() {
      setTimeout(() => {
        if (this.$refs.stripeCard && this.$refs.stripeCard.$form) {
          let form = this.$refs.stripeCard.$form;
          let cardInput = form.querySelector("#cardNumber");
          cardInput.maxlength = 19;
          cardInput.setAttribute("style", "min-width: 8em !important");
        } else {
          this.setStripeCreditCard();
        }
      }, 400);
    },
    setShadowStyles() {
      let form = this.$refs.stripeCard.$form;
      let paperInputs = form.querySelectorAll("paper-input");
      paperInputs.forEach(input => {
        const styleElement = document.createElement("style");
        styleElement.innerHTML = `iron-input > input { color: var(--paper-input-container-shared-input-style_-_color) !important; font-size: var(--paper-input-container-shared-input-style_-_font-size)  !important; }`;
        const shadow = input.shadowRoot;
        shadow.appendChild(styleElement);
      });
    },
    onClick() {
      if (this.$refs.stripeCard.$form.validate() && this.cardHolderName) {
        this.dataErrors = "";
        this.$refs.stripeCard._submit();
      } else {
        if (!this.error) {
          this.dataErrors = "These fields are required.";
        }
      }
    },
    onTokenChange(token) {
      this.token = token.detail.value;
      _set(this.token, "card.name", this.cardHolderName);
      this.isDirty = false;
      this.alertMsg = "Credit card added successfully.";
      this.alerType = "success";
      this.alert = true;
      this.$emit("input", this.token);
      this.$emit("success");
    },
    onLoadingChange(value) {
      this.saveLoading = value.detail.value || false;
      this.componentTimeout = setTimeout(() => {
        if (!this.token) {
          this.saveLoading = false;
        }
      }, 2000);
    },
    onCardInput() {
      this.isDirty = true;
      this.dataErrors = "";
    }
  },
  computed: {},
  created() {},
  mounted: async function() {
    // NOTE: stripe.js widget mount logic (Disabled) due to lack of support to ShadowRoot Dom's
    // if (!this.isStripeMount) {
    //   await this.initStripe();
    // }
    // window.onload = this.loadStripeWidget();
    await this.setStripeCreditCard();
    this.setShadowStyles();
  },
  beforeDestroy() {
    clearTimeout(this.componentTimeout);
  },
  mixins: [],
  components: {
    VRow,
    VCol,
    VCard,
    VCardTitle,
    VCardText,
    VCardActions,
    VAlert,
    VTextField,
    VBtn,
    StripeCard
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@import "../../../assets/variables";

#card-element {
  border-bottom: 1px solid rgba(0, 0, 0, 0.38);
  padding: 0.5rem;
}

.StripeElement--focus {
  border-bottom: 2px solid $colorPrimaryBlue !important;
}

.outlined-field {
  border: 1px solid rgba(0, 0, 0, 0.38);
  border-radius: 5px;
}

.w-100 {
  width: 100%;
}

.payment-card-title {
  font-size: 18px;
  font-weight: 600;
}

.payment-card-footer {
  margin-left: auto;
  margin-right: 15px;
}

.error-msg {
  color: var(--stripe-card-input-invalid-color);
  font-size: 11.9px;
  min-height: 30px;
  margin-left: 10px;
}

.stripe-card {
  --paper-input-container-color: var(--stripe-card-input-color);
  --paper-input-container-focus-color: var(--stripe-card-input-focus-color);
  --paper-input-container-invalid-color: var(--stripe-card-input-invalid-color);
  --paper-input-container-shared-input-style_-_color: var(
    --stripe-card-input-color
  );
  --paper-input-container-shared-input-style_-_font-size: var(
    --stripe-card-input-font-size
  );
}

.alert-notification {
  height: 40px;
  color: white;
  position: absolute;
  z-index: 99;
  top: 40px;
  width: 100%;
}
</style>

<style lang="scss">
@import "../../../assets/variables";

.card-payment-information {
  .alert-notification {
    padding-left: 25px;
    padding-right: 25px;

    i {
      color: white !important;
    }
  }
  .cardholder-name {
    .v-label {
      margin-left: 0;
    }

    input {
      border-bottom: 1px solid var(--primary-color);
    }
  }

  .cardholder-required {
    .v-label {
      color: var(--stripe-card-input-invalid-color) !important;
    }

    input {
      outline: none;
      border-bottom: 2px solid var(--stripe-card-input-invalid-color) !important;

      &:focus {
        border-bottom: 2px solid var(--stripe-card-input-invalid-color);
      }
    }
  }
}
</style>
